import { clientLogger } from '@/lib/clientLogger';
import { useState, useMemo, useCallback } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
    Elements,
    PaymentElement,
    useStripe,
    useElements,
} from '@stripe/react-stripe-js';
import { X, Lock, AlertCircle, Pencil, Check, FileText } from 'lucide-react';
import { ENV } from '@/config/environment';
import { getAppOrigin } from '@/lib/capacitor';
import { usePaymentStateMachine } from '@/hooks/usePaymentStateMachine';
import { PaymentResult } from './PaymentResult';
import { apiService } from '@/lib/api';
import { NumericInput } from './NumericInput';
import { withBackNavigation } from '@/lib/withBackNavigation';
import type { ModalLayer } from '@/lib/layerManager';
import type { PaymentTarget } from '@/types';

interface StripePaymentModalProps {
    isOpen: boolean;
    onClose: () => void;
    /** Job or estimate being charged; omitted for individual (job-less) payments. */
    target?: PaymentTarget;
    companyId?: string;
    connectedAccountId?: string;
    /** Called when Stripe confirms payment client-side. */
    onSuccess: () => void;
    /** Called when user dismisses the success screen (closes modal). */
    onDismiss: () => void;
    totalAmount: number;
    layer?: ModalLayer;
}

/**
 * Stripe payment modal using the deferred-intent pattern.
 *
 * - Opens with PaymentElement (no PaymentIntent exists yet).
 * - On "Confirm Payment": creates PaymentIntent server-side, then confirms client-side.
 * - DB Payment row is NEVER created here — only by the Stripe webhook.
 */
function StripePaymentModalBase({
    isOpen,
    onClose,
    target,
    companyId,
    connectedAccountId,
    onSuccess,
    onDismiss,
    totalAmount,
    layer,
}: StripePaymentModalProps) {
    if (!isOpen) return null;

    // Initialize Stripe with the connected account if present
    const stripePromise = useMemo(() => {
        if (!ENV.STRIPE_PUBLISHABLE_KEY) return null;

        try {
            const options = {
                developerTools: {
                    assistant: {
                        enabled: false
                    }
                },
                ...(connectedAccountId && { stripeAccount: connectedAccountId }),
            };

            const promise = loadStripe(ENV.STRIPE_PUBLISHABLE_KEY, options);
            // Ensure the promise is properly caught if loadStripe fails
            return promise.catch((err) => {
                clientLogger.error('Failed to initialize Stripe:', err);
                return null;
            });
        } catch (err) {
            clientLogger.error('Error loading Stripe:', err);
            return Promise.resolve(null);
        }
    }, [connectedAccountId]);

    // Handle missing key gracefully
    if (!ENV.STRIPE_PUBLISHABLE_KEY) {
        return (
            <div className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 keyboard-aware-overlay" style={layer?.style}>
                <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 relative animate-in fade-in zoom-in-95 duration-200">
                    <button
                        onClick={onClose}
                        className="absolute right-4 top-4 text-gray-400 hover:text-gray-500 transition-colors"
                    >
                        <X className="h-5 w-5" />
                    </button>
                    <div className="text-center space-y-3 pt-2">
                        <AlertCircle className="h-10 w-10 text-red-500 mx-auto" />
                        <h3 className="text-lg font-semibold text-gray-900">Configuration Error</h3>
                        <p className="text-gray-600">Stripe publishable key is missing in configuration.</p>
                        <button onClick={onClose} className="btn-secondary w-full mt-4">Close</button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 keyboard-aware-overlay" style={{ maxHeight: '100dvh', ...layer?.style }}>
            <div className="bg-white rounded-xl shadow-xl w-full max-w-lg relative animate-in fade-in zoom-in-95 duration-200 flex flex-col keyboard-constrained-modal" style={{ maxHeight: 'min(90dvh, calc(100dvh - 32px))' }}>
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-100 shrink-0">
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        Secure Payment
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-1 text-gray-400 hover:text-gray-500 hover:bg-gray-100 rounded-full transition-all"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Content — deferred intent: mode + amount, no clientSecret yet */}
                <div className="p-6 overflow-y-auto flex-1 -webkit-overflow-scrolling: touch" style={{ overscrollBehavior: 'contain' }}>
                    <Elements
                        stripe={stripePromise}
                        options={{
                            mode: 'payment',
                            amount: Math.round(totalAmount * 100),
                            currency: 'usd',
                            appearance: {
                                theme: 'stripe' as const,
                                variables: { colorPrimary: '#2563eb' },
                            },
                        }}
                    >
                        <PaymentForm
                            target={target}
                            companyId={companyId}
                            onSuccess={onSuccess}
                            onDismiss={onDismiss}
                            totalAmount={totalAmount}
                            onClose={onClose}
                        />
                    </Elements>
                </div>
            </div>
        </div>
    );
}

export const StripePaymentModal = withBackNavigation(StripePaymentModalBase);

function PaymentForm({
    target,
    companyId,
    onSuccess,
    onDismiss,
    totalAmount,
    onClose,
}: {
    target?: PaymentTarget;
    companyId?: string;
    onSuccess: () => void;
    onDismiss: () => void;
    totalAmount: number;
    onClose: () => void;
}) {
    const stripe = useStripe();
    const elements = useElements();
    const { state, errorMessage, transitionTo, reset, isFormDisabled } = usePaymentStateMachine();

    // Amount editing — purely local state, used when creating the intent
    const [isEditing, setIsEditing] = useState(false);
    const [editAmount, setEditAmount] = useState<number>(totalAmount);
    const [currentAmount, setCurrentAmount] = useState(totalAmount);
    const [localError, setLocalError] = useState<string | null>(null);
    // Notes — managed internally, shown only for individual (job-less) payments
    const [notes, setNotes] = useState('');

    const handleSaveAmount = () => {
        const newAmount = editAmount;
        if (newAmount <= 0) {
            setLocalError('Please enter a valid amount greater than 0');
            return;
        }
        setCurrentAmount(newAmount);
        setIsEditing(false);
        setLocalError(null);
    };

    /**
     * Confirm Payment — the core lazy-intent flow:
     *   1. Validate card input with elements.submit()
     *   2. Create PaymentIntent server-side (POST /api/{jobs|estimates}/{id}/create-intent)
     *   3. Confirm with Stripe client-side using the returned client_secret
     *   4. Show success on Stripe confirmation — webhook handles DB persistence
     */
    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        // Double-submit guard: only allow submission from idle state
        if (state !== 'idle' || !stripe || !elements || isEditing) {
            return;
        }

        transitionTo('processing');
        setLocalError(null);

        try {
            // Validate Stripe is ready
            if (!stripe || !elements) {
                transitionTo('failed', 'Payment system is not ready. Please try again.');
                return;
            }

            // Step 1: Validate the PaymentElement form
            const { error: submitError } = await elements.submit();
            if (submitError) {
                transitionTo('failed', submitError.message ?? 'Please check your card details.');
                return;
            }

            // Step 2: Create PaymentIntent server-side (NO DB row created)
            let client_secret: string;
            try {
                const response = target
                    ? await apiService.createPaymentIntent(target, currentAmount)
                    : await apiService.createIndividualPaymentIntent(companyId!, currentAmount, notes || undefined);
                client_secret = response.client_secret;
            } catch (apiErr: any) {
                transitionTo('failed', apiErr.message || 'Failed to initialize payment. Please try again.');
                return;
            }

            // Step 3: Confirm with Stripe
            let result: any;
            try {
                result = await stripe.confirmPayment({
                    elements,
                    clientSecret: client_secret,
                    confirmParams: {
                        return_url: getAppOrigin() + '/payment-complete',
                    },
                    redirect: 'if_required',
                });
            } catch (stripeErr: any) {
                transitionTo('failed', stripeErr.message || 'Payment confirmation failed. Please try again.');
                return;
            }

            if (result.error) {
                transitionTo('failed', result.error.message ?? 'An unexpected error occurred.');
            } else if (result.paymentIntent?.status === 'succeeded') {
                // Stripe confirmed client-side — show success immediately.
                // Webhook will create the DB Payment row asynchronously.
                transitionTo('succeeded');
                onSuccess();
            } else if (result.paymentIntent?.status === 'processing') {
                // Long-running (e.g. bank transfers) — show processing state
                // Webhook will finalize
                transitionTo('succeeded');
                onSuccess();
            } else {
                transitionTo('failed', 'Payment processing incomplete. Please try again.');
            }
        } catch (err: any) {
            transitionTo('failed', err.message || 'An unexpected error occurred during processing.');
        }
    };

    const handleRetry = useCallback(() => {
        reset();
        setLocalError(null);
    }, [reset]);

    // ── Render based on state ──────────────────────────────────────────

    if (state === 'succeeded') {
        return (
            <PaymentResult
                variant="success"
                amount={currentAmount}
                primaryLabel={target ? `Back to ${target.kind === 'job' ? 'Job' : 'Estimate'}` : "Done"}
                onPrimaryAction={onDismiss}
            />
        );
    }

    if (state === 'failed') {
        return (
            <PaymentResult
                variant="error"
                amount={currentAmount}
                message={errorMessage ?? undefined}
                primaryLabel="Try Again"
                onPrimaryAction={handleRetry}
                secondaryLabel="Close"
                onSecondaryAction={onClose}
            />
        );
    }

    // ── idle / processing: keep form mounted so PaymentElement stays in DOM ──

    const displayError = localError || errorMessage;
    const isProcessing = state === 'processing';

    return (
        <div className="relative">
            {/* Processing overlay — form stays mounted underneath so PaymentElement isn't unmounted */}
            {isProcessing && (
                <div className="fixed inset-0 z-50 bg-white/80 backdrop-blur-[2px] flex items-center justify-center" style={{ pointerEvents: 'auto' }}>
                    <PaymentResult variant="processing" amount={currentAmount} />
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 relative">
                    <div className="flex justify-between items-center mb-4 min-h-[40px]">
                        <span className="text-sm font-medium text-gray-700">Total to Pay</span>

                        {isEditing ? (
                            <div className="flex items-center gap-2">
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">$</span>
                                    <NumericInput
                                        value={editAmount}
                                        onChange={(val) => setEditAmount(val || 0)}
                                        className="pl-6 w-32 py-1 px-2 text-right font-bold text-gray-900 border rounded-md focus:ring-2 focus:ring-blue-500 outline-none"
                                        step="0.01"
                                        min="0.01"
                                        inputMode="decimal"
                                        autoCorrect="off"
                                        autoCapitalize="off"
                                    />
                                </div>
                                <button
                                    type="button"
                                    onClick={handleSaveAmount}
                                    className="p-1.5 text-green-600 hover:bg-green-50 rounded-full transition-colors"
                                    title="Save amount"
                                >
                                    <Check className="h-4 w-4" />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setIsEditing(false);
                                        setEditAmount(currentAmount);
                                        setLocalError(null);
                                    }}
                                    className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                                    title="Cancel"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                        ) : (
                            <div className="flex items-center gap-2">
                                <span className="text-xl font-bold text-gray-900">${currentAmount.toFixed(2)}</span>
                                <button
                                    type="button"
                                    onClick={() => setIsEditing(true)}
                                    className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-all"
                                    title="Edit amount"
                                >
                                    <Pencil className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        )}
                    </div>

                    <PaymentElement />
                </div>

                {/* Notes — only for individual (job-less) payments */}
                {!target && (
                    <div>
                        <label htmlFor="stripe-payment-notes" className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1">
                            <FileText className="h-3.5 w-3.5" />
                            Notes
                            <span className="text-gray-400 font-normal">(optional)</span>
                        </label>
                        <textarea
                            id="stripe-payment-notes"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="What is this payment for?"
                            rows={2}
                            maxLength={500}
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck="false"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 resize-none transition-colors"
                        />
                    </div>
                )}

                {displayError && (
                    <div className="p-3 bg-red-50 text-red-700 text-sm rounded-md flex items-center gap-2">
                        <AlertCircle className="h-4 w-4 shrink-0" />
                        {displayError}
                    </div>
                )}

                <button
                    type="submit"
                    disabled={!stripe || isFormDisabled || isEditing}
                    className="w-full flex justify-center items-center gap-2 min-h-[44px] py-2.5 px-4 border border-transparent rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                >
                    {`Pay $${currentAmount.toFixed(2)} `}
                    <Lock className="h-4 w-4 opacity-80" />
                </button>

                <p className="text-xs text-center text-gray-500 mt-2">
                    Payments are securely processed by Stripe.
                </p>
            </form>
        </div>
    );
}
